import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

/**
 * Tracks how many times this browser has seen a fresh SIGNED_IN event for the
 * current user. Used by `ChatBubble` to decide whether to show the friendly
 * "say hi to support" Pikachu (new accounts < 10 sign-ins) or the tasteful
 * default Bell/Mail icon (returning users).
 *
 * Per-tab debounce via sessionStorage so a refresh doesn't double-count.
 */
const COUNT_KEY = (uid: string) => `cs.auth.signinCount.v1:${uid}`;
const SESSION_FLAG = (uid: string) => `cs.auth.signinCounted.v1:${uid}`;

export function readSigninCount(userId: string | undefined | null): number {
	if (!userId || typeof window === 'undefined') return 0;
	try {
		const raw = window.localStorage.getItem(COUNT_KEY(userId));
		const n = raw ? parseInt(raw, 10) : 0;
		return Number.isFinite(n) ? n : 0;
	} catch {
		return 0;
	}
}

function bump(userId: string) {
	try {
		if (window.sessionStorage.getItem(SESSION_FLAG(userId)) === '1') return;
		const current = readSigninCount(userId);
		window.localStorage.setItem(COUNT_KEY(userId), String(current + 1));
		window.sessionStorage.setItem(SESSION_FLAG(userId), '1');
	} catch {}
}

export function useSigninCounter() {
	useEffect(() => {
		// Count the existing session on mount (covers page reloads where
		// onAuthStateChange may not fire SIGNED_IN again).
		supabase.auth.getUser().then(({ data }) => {
			if (data.user) bump(data.user.id);
		});

		const { data: sub } = supabase.auth.onAuthStateChange((event, session) => {
			if (event === 'SIGNED_IN' && session?.user) {
				bump(session.user.id);
			}
		});
		return () => {
			sub.subscription.unsubscribe();
		};
	}, []);
}
